import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import url from '../serverUrl';

const API_URL = url;

const CreatePDCNTokenForm = () => {
  const [formData, setFormData] = useState({
    symbol: '',
    name: '',
    description: '',
    price: '',
    base_symbol: '',
    base_power: '',
    contract_reference: '',
    issuer: '',
    staking_wallet: '',
    expiry_type: 'Perpetual',
    maturity_date: null,
    annual_yield: '',
    yield_redemption: 'Quarterly',
    collateral_backing: '',
    early_redemption: '',
    conversion_parameters: '',
    mandatory_hold: '12',
    issuance_fees: '100',
    oracle_service_fees: '10',
    logo: '',
    amount: 0
  });

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileForm = new FormData();
    fileForm.append('image', file);
    fileForm.append('fileName', file.name);

    setUploading(true);
    try {
      const res = await axios.post(
        `${API_URL}/hootdex/token-logo-upload`,
        fileForm
      );
      if (res.data.status === 'ok') {
        setFormData((prev) => ({
          ...prev,
          logo: res.data.fileName
        }));
        setAlert({ open: true, message: 'Logo uploaded', severity: 'success' });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setAlert({
        open: true,
        message: 'Logo upload failed',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlert({ open: false, message: '', severity: 'success' });

    try {
      const payload = {
        ...formData,
        logo: formData.logo ? `${API_URL}/hootdex/images/${formData.logo}` : '',
        price: parseFloat(formData.price || 0),
        base_power: parseInt(formData.base_power || 1),
        face_value:
          parseFloat(formData.price || 0) * parseInt(formData.base_power || 1),
        daily_yield: parseFloat(formData.annual_yield || 0) / 365
      };

      await axios.post(`${API_URL}/pdcn/create`, payload);

      setAlert({
        open: true,
        message: 'PDCN Token created successfully',
        severity: 'success'
      });

      setFormData({
        symbol: '',
        name: '',
        description: '',
        price: '',
        base_symbol: '',
        base_power: '',
        contract_reference: '',
        issuer: '',
        staking_wallet: '',
        expiry_type: 'Perpetual',
        maturity_date: '',
        annual_yield: '',
        yield_redemption: 'Quarterly',
        collateral_backing: '',
        early_redemption: '',
        conversion_parameters: '',
        mandatory_hold: '12',
        issuance_fees: '100',
        oracle_service_fees: '10',
        logo: '',
        amount: 0
      });
    } catch (error) {
      setAlert({
        open: true,
        message:
          error.response?.data?.details ||
          error.response?.data?.error ||
          error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 700, mx: 'auto', mt: 4, p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Create PDCN Token
      </Typography>

      {formData.logo && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Avatar
            src={`${API_URL}/hootdex/images/${formData.logo}`}
            alt="Token Logo"
            sx={{ width: 72, height: 72 }}
          />
        </Box>
      )}

      <Box display="flex" justifyContent="center" mt={2} mb={3}>
        <Button variant="outlined" component="label" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Logo'}
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
          />
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {[
          { label: 'PDCN Symbol *', name: 'symbol' },
          { label: 'PDCN Name *', name: 'name' },
          { label: 'Description', name: 'description' },
          { label: 'Price (USD)', name: 'price' },
          { label: 'Oracle Symbol', name: 'base_symbol' },
          { label: 'Base Power (x)', name: 'base_power' },
          { label: 'Contract Reference', name: 'contract_reference' },
          { label: 'Issuer Name', name: 'issuer' },
          { label: 'Staking Wallet Public Key', name: 'staking_wallet' },
          { label: 'Annual Yield (%)', name: 'annual_yield' },
          { label: 'Collateral Backing', name: 'collateral_backing' },
          { label: 'Early Redemption', name: 'early_redemption' },
          { label: 'Conversion Parameters', name: 'conversion_parameters' }
        ].map(({ label, name }) => (
          <TextField
            key={name}
            fullWidth
            label={label}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            variant="outlined"
            margin="normal"
          />
        ))}

        {/* Expiry Type */}
        <FormControl margin="normal">
          <FormLabel>Expiry</FormLabel>
          <RadioGroup
            row
            name="expiry_type"
            value={formData.expiry_type}
            onChange={handleChange}
          >
            <FormControlLabel
              value="Perpetual"
              control={<Radio />}
              label="Perpetual"
            />
            <FormControlLabel
              value="Maturity Date"
              control={<Radio />}
              label="Maturity Date"
            />
          </RadioGroup>
        </FormControl>

        {formData.expiry_type === 'Maturity Date' && (
          <TextField
            fullWidth
            type="date"
            name="maturity_date"
            value={formData.maturity_date}
            onChange={handleChange}
            margin="normal"
          />
        )}

        {/* Yield Redemptions */}
        <FormControl margin="normal">
          <FormLabel>Yield Token Redemptions</FormLabel>
          <RadioGroup
            row
            name="yield_redemption"
            value={formData.yield_redemption}
            onChange={handleChange}
          >
            <FormControlLabel
              value="Quarterly"
              control={<Radio />}
              label="Quarterly"
            />
            <FormControlLabel
              value="Semi-Annual"
              control={<Radio />}
              label="Semi-Annual"
            />
          </RadioGroup>
        </FormControl>

        {/* Mandatory Hold */}
        <FormControl margin="normal">
          <FormLabel>Mandatory Hold Period</FormLabel>
          <RadioGroup
            row
            name="mandatory_hold"
            value={formData.mandatory_hold}
            onChange={handleChange}
          >
            <FormControlLabel
              value="12"
              control={<Radio />}
              label="12 Months"
            />
            <FormControlLabel
              value="18"
              control={<Radio />}
              label="18 Months"
            />
            <FormControlLabel
              value="24"
              control={<Radio />}
              label="24 Months"
            />
          </RadioGroup>
        </FormControl>

        {/* Fees */}
        <TextField
          fullWidth
          label="Issuance Fees (PECU)"
          name="issuance_fees"
          value={formData.issuance_fees}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Oracle Service Fees (PECU)"
          name="oracle_service_fees"
          value={formData.oracle_service_fees}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Amount"
          name="amount"
          value={formData.amount}
          onChange={handleChange}
          margin="normal"
        />

        <Box mt={3} textAlign="right">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || uploading}
            startIcon={loading && <CircularProgress size={18} />}
          >
            {loading ? 'Creating...' : 'Create Token'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={alert.severity}
          variant="filled"
          onClose={() => setAlert({ ...alert, open: false })}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CreatePDCNTokenForm;
